/**
 * Shift activity heartbeat.
 *
 * Called from conversation actions/interactions to keep an active shift session
 * alive. The server decides whether a shift is currently active and no-ops when
 * there is none, which avoids a race right after Start while the gate query is
 * still hydrating.
 */
import { useCallback, useRef } from "react";
import { useMutation } from "convex/react";

import { api } from "@/../convex/_generated/api";
import { useActiveAccountId } from "@/core/auth/activeAccount";

const HEARTBEAT_THROTTLE_MS = 30_000;

export function useShiftActivityHeartbeat() {
  const accountId = useActiveAccountId();
  const record = useMutation(api.shiftGate.recordShiftActivity);
  const lastBumpRef = useRef<number>(0);

  return useCallback(() => {
    if (!accountId) return;
    const now = Date.now();
    if (now - lastBumpRef.current < HEARTBEAT_THROTTLE_MS) return;
    lastBumpRef.current = now;

    void record({ accountId }).catch(() => {
      lastBumpRef.current = 0;
    });
  }, [accountId, record]);
}
